// formatBackendError — translates raw backend Zod error strings into
// user-facing copy on the ManagedContractForm surface. The backend Zod
// schema emits messages like
//   "contractPeople.0.email: email is required when alertsEnabled is true"
// which are accurate but read as gibberish to a user. Known patterns
// rewrite to friendly text; anything unmatched falls through unchanged so
// we never swallow information.

interface FormatContext {
  contractPeople?: Array<{ role: string }>;
}

function toMessage(err: unknown): string {
  if (err === null || err === undefined) return "";
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  if (typeof err === "object" && err !== null && "message" in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return String(err);
}

function titleCase(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function formatBackendError(
  err: unknown,
  context?: FormatContext,
): string {
  const message = toMessage(err);
  if (!message) return "";

  // Pattern: contractPeople.{idx}.email: email is required when
  // alertsEnabled is true
  const emailMatch = message.match(
    /contractPeople\.(\d+)\.email:\s*email is required when alertsEnabled is true/,
  );
  if (emailMatch) {
    const idx = Number(emailMatch[1]);
    const role = context?.contractPeople?.[idx]?.role ?? "person";
    return `${titleCase(role)} email is required while alerts are turned on for them. Either fill in the email or disable alerts for ${role}.`;
  }

  if (/contractPeople must contain exactly one owner/.test(message)) {
    return "A contract owner is required.";
  }

  if (/contractPeople must contain exactly one manager/.test(message)) {
    return "A contract manager is required.";
  }

  if (/manager\.alertsEnabled must be true \(server-enforced\)/.test(message)) {
    return "Manager alerts cannot be disabled.";
  }

  return message;
}
